import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Collapse,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { AuthContext } from "../authentification/AuthContext";
import { fetchChallengeComments, addChallengeComment } from "../../utils/api/api";

const CommentSection = ({ challengeId }) => {
  const { t, i18n } = useTranslation();
  const { user } = useContext(AuthContext);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedComments, setExpandedComments] = useState({});

  useEffect(() => {
    fetchChallengeComments(challengeId).then((data) => {
      setComments(data);
      setLoading(false);
    });
  }, [challengeId]);

  const toggleExpand = (commentId) => {
    setExpandedComments((prev) => ({
      ...prev,
      [commentId]: !prev[commentId],
    }));
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) return;

    const tempComment = {
      id: Math.random().toString(),
      user: { username: user.username, profile_picture: user.profile_picture, first_name: user.first_name },
      text: newComment,
      replies: [],
      parent: replyTo ? replyTo.id : null,
    };

    setComments((prevComments) =>
      replyTo
        ? prevComments.map((comment) =>
            comment.id === replyTo.id
              ? { ...comment, replies: [tempComment, ...comment.replies] }
              : comment
          )
        : [tempComment, ...prevComments]
    );

    setNewComment("");
    setReplyTo(null);

    try {
      await addChallengeComment(challengeId, newComment, replyTo?.id);
    } catch (error) {
      setComments((prevComments) => prevComments.filter((c) => c.id !== tempComment.id));
    }
  };

  return (
    <Box p={2} borderRadius={2} bgcolor="#f9f9f9" border={2} mt={3} sx={{ borderColor: '#5ca57e' }}>
      <Typography variant="h6" gutterBottom>
        💬 The chat
      </Typography>
      {loading ? (
        <Typography>Loading comments...</Typography>
      ) : (
        <List>
          {comments.map((comment) => (
            <Box key={comment.id} mb={1} sx={{borderColor: expandedComments[comment.id] ? '#cecece' : null, borderWidth: expandedComments[comment.id] ? 1 : 0, borderRadius: '10px'}}>
              <ListItem
                sx={{
                  display: "flex",
                  justifyContent: comment.user.username === user.username ? "flex-end" : "flex-start",
                }}
              >
                {comment.user.username !== user.username && (
                  <ListItemAvatar>
                    <Avatar src={comment.user.profile_picture}>{comment.user.first_name.charAt(0)}</Avatar>
                  </ListItemAvatar>
                )}
                <Box
                  sx={{
                    bgcolor: comment.user.username === user.username ? "#5ca57e30" : "#f1f1f1",
                    p: 1.5,
                    borderRadius: 3,
                    maxWidth: "70%",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="bold">
                    {comment.user.first_name}
                  </Typography>
                  <Typography>{comment.text}</Typography>
                  <Box display="flex" alignItems="center" justifyContent={'space-between'}>
                    <Button size="small" onClick={() => setReplyTo(comment)}>Reply</Button>
                    {comment.replies.length > 0 && (
                      <IconButton onClick={() => toggleExpand(comment.id)}>
                        {expandedComments[comment.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    )}
                  </Box>
                </Box>
                {comment.user.username === user.username && (
                  <ListItemAvatar sx={{ marginLeft: 2 }}>
                    <Avatar src={comment.user.profile_picture}>{comment.user.first_name.charAt(0)}</Avatar>
                  </ListItemAvatar>
                )}
              </ListItem>
              <Collapse in={expandedComments[comment.id]} timeout="auto" unmountOnExit>
                <List sx={{ paddingLeft: 4 }}>
                  {comment.replies.map((reply) => (
                    <ListItem key={reply.id}>
                      <ListItemAvatar>
                        <Avatar src={reply.user.profile_picture}>{reply.user.first_name.charAt(0)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={reply.user.first_name}
                        secondary={reply.text}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          ))}
        </List>
      )}
      <Box display="flex" mt={2} gap={2}>
        <TextField
          fullWidth
          placeholder={replyTo ? `${t('challenge.reply-to')} ${replyTo.user.first_name}...` : t('challenge.comment')}
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
        />
        <IconButton color="primary" onClick={handlePostComment}>
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CommentSection;
